import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import api from "../../services/api";
import { Card, EmptyState, TabHeading, UpgradeCard } from "./StudioShared";

const LANGS = ["en", "fr", "es", "ar"];

function EpisodeUploader({ id, podcastId, episode, onDone }) {
  const { t } = useTranslation();
  const [pct, setPct] = useState(null);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("audio/")) { setError(t("podcasts.uploadFailed")); return; }
    setError("");
    setPct(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/api/radios/${id}/podcasts/${podcastId}/episodes/${episode.id}/media`, fd, {
        onUploadProgress: (e) => setPct(e.total ? Math.round((e.loaded / e.total) * 100) : 0),
      });
      setPct(null);
      onDone();
    } catch (err) {
      const status = err?.response?.status;
      setError(status === 413 ? t("podcasts.storageExceeded") : t("podcasts.uploadFailed"));
      setPct(null);
    }
  };

  return (
    <div className="text-end">
      <input
        ref={inputRef}
        type="file"
        accept="audio/*"
        className="hidden"
        onChange={(e) => { if (e.target.files?.[0]) upload(e.target.files[0]); e.target.value = ""; }}
      />
      {pct === null ? (
        <button onClick={() => inputRef.current?.click()} className="text-xs bg-gray-100 hover:bg-purple-100 hover:text-purple-700 text-gray-600 px-2.5 py-1.5 rounded-md">
          ⬆ {t("podcasts.uploadAudio")}
        </button>
      ) : (
        <div className="w-28">
          <div className="h-1.5 rounded-full bg-gray-100 overflow-hidden">
            <div className="h-full bg-purple-500" style={{ width: `${pct}%` }} />
          </div>
          <span className="text-[10px] text-gray-400">{t("podcasts.uploading")} {pct}%</span>
        </div>
      )}
      {error && <p className="text-[10px] text-red-500 mt-1">{error}</p>}
    </div>
  );
}

function PodcastCard({ id, podcast, onChanged }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [episodes, setEpisodes] = useState(null);
  const [addingEp, setAddingEp] = useState(false);
  const [epTitle, setEpTitle] = useState("");
  const [epDesc, setEpDesc] = useState("");

  const loadEpisodes = useCallback(async () => {
    try {
      const r = await api.get(`/api/radios/${id}/podcasts/${podcast.id}/episodes`);
      setEpisodes(Array.isArray(r.data) ? r.data : []);
    } catch { setEpisodes([]); }
  }, [id, podcast.id]);

  useEffect(() => { if (open && episodes === null) loadEpisodes(); }, [open, episodes, loadEpisodes]);

  const createEpisode = async () => {
    if (!epTitle.trim()) return;
    try {
      await api.post(`/api/radios/${id}/podcasts/${podcast.id}/episodes`, {
        title: epTitle.trim(),
        description: epDesc.trim(),
      });
      setEpTitle(""); setEpDesc(""); setAddingEp(false);
      await loadEpisodes();
    } catch { /* ignore */ }
  };

  const deleteEpisode = async (eid) => {
    if (!window.confirm(t("podcasts.confirmDeleteEpisode"))) return;
    try {
      await api.delete(`/api/radios/${id}/podcasts/${podcast.id}/episodes/${eid}`);
      await loadEpisodes();
    } catch { /* ignore */ }
  };

  const removePodcast = async () => {
    if (!window.confirm(t("podcasts.confirmDelete"))) return;
    try {
      await api.delete(`/api/radios/${id}/podcasts/${podcast.id}`);
      onChanged();
    } catch { /* ignore */ }
  };

  return (
    <Card>
      <div className="flex items-center gap-3 flex-wrap">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-blue-500 text-white flex items-center justify-center text-xl">🎧</div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-gray-900 truncate">{podcast.title}</div>
          <div className="text-xs text-gray-500 truncate">{podcast.author || "—"}{podcast.explicit ? " • 🅴" : ""}</div>
        </div>
        <button onClick={() => setOpen((o) => !o)} className="text-sm bg-purple-50 text-purple-700 px-3 py-1.5 rounded-md hover:bg-purple-100">
          {t("podcasts.episodes")}
        </button>
        <button onClick={removePodcast} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-600 flex items-center justify-center">✕</button>
      </div>

      {open && (
        <div className="mt-4 border-t border-gray-100 pt-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-700">{t("podcasts.episodes")}</h4>
            <button onClick={() => setAddingEp((a) => !a)} className="text-xs bg-purple-600 hover:bg-purple-700 text-white px-3 py-1.5 rounded-md">
              ＋ {t("podcasts.addEpisode")}
            </button>
          </div>

          {addingEp && (
            <div className="rounded-xl border border-gray-100 p-4 bg-gray-50/50 mb-3 space-y-2">
              <input value={epTitle} onChange={(e) => setEpTitle(e.target.value)} placeholder={t("podcasts.episodeTitle")} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm" />
              <textarea value={epDesc} onChange={(e) => setEpDesc(e.target.value)} placeholder={t("podcasts.episodeDescription")} rows={2} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm" />
              <div className="flex gap-2">
                <button onClick={createEpisode} disabled={!epTitle.trim()} className="text-sm bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md disabled:opacity-50">{t("podcasts.createEpisode")}</button>
                <button onClick={() => setAddingEp(false)} className="text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md">{t("studio.actions.cancel")}</button>
              </div>
            </div>
          )}

          {episodes === null ? (
            <p className="text-sm text-gray-500">{t("studio.loading")}</p>
          ) : episodes.length === 0 ? (
            <p className="text-sm text-gray-500">{t("podcasts.noEpisodes")}</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {episodes.map((ep) => {
                const hasAudio = !!(ep.has_media || ep.media || ep.playlist_media_id || ep.length);
                return (
                  <li key={ep.id} className="flex items-center gap-3 py-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-gray-900 truncate">{ep.title}</div>
                      <div className="text-xs text-gray-500">{hasAudio ? `✓ ${t("podcasts.hasAudio")}` : t("podcasts.noAudio")}</div>
                    </div>
                    <EpisodeUploader id={id} podcastId={podcast.id} episode={ep} onDone={loadEpisodes} />
                    <button onClick={() => deleteEpisode(ep.id)} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-red-100 text-gray-500 hover:text-red-600 flex items-center justify-center">✕</button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </Card>
  );
}

export default function PodcastsTab({ id, locked }) {
  const { t } = useTranslation();
  const [podcasts, setPodcasts] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", author: "", email: "", language: "en", explicit: false });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api.get(`/api/radios/${id}/podcasts`);
      setPodcasts(Array.isArray(r.data) ? r.data : []);
    } catch { setPodcasts([]); }
  }, [id]);

  useEffect(() => { if (!locked) load(); }, [locked, load]);

  if (locked) {
    return <UpgradeCard title={t("studio.locked.podcastsTitle")} desc={t("studio.locked.podcastsDesc")} />;
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const create = async () => {
    if (!form.title.trim()) return;
    setCreating(true);
    try {
      await api.post(`/api/radios/${id}/podcasts`, {
        title: form.title.trim(),
        description: form.description.trim(),
        author: form.author.trim(),
        email: form.email.trim(),
        language: form.language,
        explicit: form.explicit,
        categories: [],
      });
      setForm({ title: "", description: "", author: "", email: "", language: "en", explicit: false });
      setAdding(false);
      await load();
    } catch { /* ignore */ } finally {
      setCreating(false);
    }
  };

  return (
    <div>
      <TabHeading
        title={t("podcasts.title")}
        subtitle={t("podcasts.subtitle")}
        action={
          <button onClick={() => setAdding((a) => !a)} className="text-sm bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md">
            ＋ {t("podcasts.add")}
          </button>
        }
      />

      {adding && (
        <Card className="mb-5 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-xs text-gray-500 sm:col-span-2">
              <span className="block mb-1">{t("podcasts.form.title")}</span>
              <input value={form.title} onChange={(e) => set("title", e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm" />
            </label>
            <label className="text-xs text-gray-500 sm:col-span-2">
              <span className="block mb-1">{t("podcasts.form.description")}</span>
              <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={2} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm" />
            </label>
            <label className="text-xs text-gray-500">
              <span className="block mb-1">{t("podcasts.form.author")}</span>
              <input value={form.author} onChange={(e) => set("author", e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm" />
            </label>
            <label className="text-xs text-gray-500">
              <span className="block mb-1">{t("podcasts.form.email")}</span>
              <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm" />
            </label>
            <label className="text-xs text-gray-500">
              <span className="block mb-1">{t("podcasts.form.language")}</span>
              <select value={form.language} onChange={(e) => set("language", e.target.value)} className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm bg-white">
                {LANGS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-600 mt-6">
              <input type="checkbox" checked={form.explicit} onChange={(e) => set("explicit", e.target.checked)} />
              {t("podcasts.form.explicit")}
            </label>
          </div>
          <div className="flex gap-2">
            <button onClick={create} disabled={creating || !form.title.trim()} className="text-sm bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-md disabled:opacity-50">{t("podcasts.create")}</button>
            <button onClick={() => setAdding(false)} className="text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-md">{t("studio.actions.cancel")}</button>
          </div>
        </Card>
      )}

      {podcasts === null ? (
        <p className="text-gray-500">{t("studio.loading")}</p>
      ) : podcasts.length === 0 ? (
        <Card>
          <EmptyState icon="🎧" title={t("podcasts.empty")} hint={t("podcasts.emptyHint")} />
        </Card>
      ) : (
        <div className="space-y-3">
          {podcasts.map((p) => <PodcastCard key={p.id} id={id} podcast={p} onChanged={load} />)}
        </div>
      )}
    </div>
  );
}
